<template>
  <div
    v-if="enabledAppearanceGroups.length"
    class="product-settings-appearance-groups"
  >
    <SelectionTiles
      v-slot="{ item }"
      :items="enabledAppearanceGroups"
      :item-key="(appearanceGroup) => appearanceGroup.id"
      :item-selected="(item) => item.id === currentAppearanceGroup.id"
      :item-to-string="(item) => item.name"
      @change="selectAppearanceGroup"
    >
      <ColorCircle v-if="item.uiColor" :color="item.uiColor" :size="24" />
      <span v-else>
        <Icon icon="palette" />
      </span>
    </SelectionTiles>
    <div
      v-if="disabledAppearanceGroups.length"
      class="disabled-appearance-groups"
    >
      <strong>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEACTIVATED_COLORS.HEADING') }}
        <SmartFaqLink
          :link="smartFaqLinks.HELP_CENTER.key"
          icon-only
          class="help-link"
      /></strong>
      <p>
        {{ $t('PUBLISHING.DETAIL.PRODUCTS.DEACTIVATED_COLORS.SUBHEADING') }}
      </p>
      <SelectionTiles
        v-slot="{ item }"
        :items="disabledAppearanceGroups"
        :item-key="(appearanceGroup) => appearanceGroup.id"
        disabled
        :item-selected="() => false"
        :item-to-string="(item) => item.name"
      >
        <ColorCircle
          :color="item.uiColor"
          :size="24"
          deactivated
          hide-deactivated-icon
        />
      </SelectionTiles>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import compositionCategoryHelper from 'commons/composition/compositionCategoryHelper';
import compositionHelper from 'commons/composition/compositionHelper';
import { useStore } from 'vuex';
import ColorCircle from 'commons/colorCircle/ColorCircle.vue';
import SmartFaqLink from 'commons/helpLink/SmartFaqLink.vue';
import smartFaqLinks from 'commons/smartFaq/smartFaqLinks';
import SelectionTiles from 'src/app/components/selectionTiles/SelectionTiles.vue';
import { getAllProductTypes } from '@spreadgroup/common-partner-lib/composition';

const store = useStore();

const emit = defineEmits(['onSelect']);
const props = defineProps({
  composition: {
    type: Object,
    required: true,
  },
  selectedAppearanceGroup: {
    type: String,
  },
});

const automaticAppearanceGroup = {
  name: 'PUBLISHING.DETAIL.PRODUCTS.DEFAULT_COLOR.AUTOMATIC',
};

const productTypes = computed(() =>
  getAllProductTypes(store.state.publishingCore.categories),
);

const appearanceGroups = computed(() =>
  compositionCategoryHelper.getAllAvailableAppearanceGroups(productTypes.value),
);
const enabledAppearanceGroups = computed(() => [
  automaticAppearanceGroup,
  ...appearanceGroups.value.filter(
    (appearanceGroup) =>
      !compositionHelper
        .getDisabledAppearanceGroupIds(props.composition)
        .includes(appearanceGroup.id),
  ),
]);

const currentAppearanceGroup = computed(
  () =>
    enabledAppearanceGroups.value.find(
      (ag) => ag.id === props.selectedAppearanceGroup,
    ) ||
    compositionHelper.getDefaultAppearanceGroup(props.composition) ||
    automaticAppearanceGroup,
);
const disabledAppearanceGroups = computed(() =>
  appearanceGroups.value.filter((appearanceGroup) =>
    compositionHelper
      .getDisabledAppearanceGroupIds(props.composition)
      .includes(appearanceGroup.id),
  ),
);

const selectAppearanceGroup = (appearanceGroup) => {
  emit('onSelect', appearanceGroup.id);
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';

.appearance-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 56px;

  :deep(.color) {
    margin: 0;
  }

  span {
    text-align: center;
  }
}

.disabled-appearance-groups {
  margin-top: 24px;

  & > strong {
    display: flex;
    gap: 8px;
  }

  & > p {
    margin: 8px 0 16px 0;
  }
}
</style>
